import { useEffect, useRef, useState } from "react";
import { Truck, Users } from "lucide-react";

const GoogleGIcon = ({ className }: { className?: string; strokeWidth?: number }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

interface StatItem {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number | string }>;
  value: number;
  suffix: string;
  label: string;
  description: string;
  decimals?: number;
}

const stats: StatItem[] = [
  { icon: Truck, value: 380, suffix: "+", label: "Moves Completed", description: "Completed Across Melbourne" },
  { icon: Users, value: 380, suffix: "+", label: "Happy Customers", description: "Trusted by Local Customers" },
  { icon: GoogleGIcon, value: 5.0, suffix: "", label: "Average Rating", description: "Based on Google Reviews", decimals: 1 },
];

function useCountUp(target: number, duration: number, start: boolean, decimals = 0) {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (!start) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(parseFloat((eased * target).toFixed(decimals)));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [start, target, duration, decimals]);

  return current;
}

const StatBlock = ({ icon: Icon, value, suffix, label, description, decimals = 0, animate }: StatItem & { animate: boolean }) => {
  const count = useCountUp(value, 1800, animate, decimals);
  return (
    <div className="flex flex-col items-center text-center gap-3">
      <div className="w-12 h-12 rounded-full bg-[#FFC300]/12 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#1a1a1a]" strokeWidth={2} />
      </div>
      <div>
        <span className="text-4xl md:text-5xl font-black text-[#1a1a1a] leading-none tracking-tight">
          {decimals ? count.toFixed(decimals) : Math.round(count).toLocaleString()}{suffix}
        </span>
      </div>
      <div>
        <p className="font-bold text-[#1a1a1a] text-sm">{label}</p>
        <p className="text-[#1a1a1a]/70 text-xs mt-0.5">{description}</p>
      </div>
    </div>
  );
};

const SocialProofStats = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimate(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-white py-20 md:py-28">
      <div className="container max-w-4xl text-center">
        <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] leading-tight">
          Trusted Across Australia
        </h2>
        <p className="text-[#1a1a1a]/70 mt-2 text-base mb-14">
          The numbers behind our commitment to better moves.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 md:gap-16 max-w-3xl mx-auto">
          {stats.map((s) => (
            <StatBlock key={s.label} {...s} animate={animate} decimals={s.decimals ?? 0} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialProofStats;
